// pages/admin/PremiumAnalytics.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Divider,
  Grid,
  useTheme,
  Tab,
  Tabs,
} from "@mui/material";
import { useAdmin } from "../../context/AdminContext";
import { Plan, Workspace, User } from "../../context/types";
import { THEME_COLORS } from "../../context/constants";

interface PlanRevenue {
  name: string;
  count: number;
  revenue: number;
  videoLimit: number;
  pricePerUGC: number;
}

export const PremiumAnalytics: React.FC = () => {
  const { premiumUsers, premiumWorkspaces, loading, fetchPremiumData } =
    useAdmin();
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [planStats, setPlanStats] = useState<PlanRevenue[]>([]);
  const [totalRevenue, setTotalRevenue] = useState(0);

  useEffect(() => {
    fetchPremiumData();
  }, []);

  useEffect(() => {
    if (premiumWorkspaces.length > 0) {
      calculateRevenue();
    }
  }, [premiumWorkspaces]);

  const calculateRevenue = () => {
    const plansMap: Record<string, PlanRevenue> = {};

    premiumWorkspaces.forEach((workspace) => {
      if (!workspace.plan) return;

      const planName = workspace.plan.name;
      if (!plansMap[planName]) {
        plansMap[planName] = {
          name: planName,
          count: 0,
          revenue: 0,
          videoLimit: workspace.plan.videoLimit,
          pricePerUGC: workspace.plan.pricePerUGC,
        };
      }

      plansMap[planName].count += 1;
      plansMap[planName].revenue +=
        workspace.plan.videoLimit * workspace.plan.pricePerUGC;
    });

    const calculatedStats = Object.values(plansMap);
    setPlanStats(calculatedStats);
    setTotalRevenue(
      calculatedStats.reduce((sum, plan) => sum + plan.revenue, 0)
    );
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Premium Analytics
      </Typography>
      <Typography
        variant="h6"
        gutterBottom
        sx={{ mb: 3, color: THEME_COLORS.darkYellow }}
      >
        {"(stats under developement)"}
      </Typography>

      {/* Revenue Summary Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: "100%" }}>
            <Typography variant="h6" gutterBottom>
              Total Monthly Revenue
            </Typography>
            <Typography variant="h3" color={theme.palette.primary.main}>
              ${totalRevenue.toLocaleString()}
            </Typography>
          </Paper>
        </Grid>

        {planStats.map((plan) => (
          <Grid key={plan.name} item xs={12} md={4}>
            <Paper sx={{ p: 3, height: "100%" }}>
              <Typography variant="h6" gutterBottom>
                {plan.name.charAt(0).toUpperCase() + plan.name.slice(1)} Plans
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  <Typography variant="body1">Workspaces:</Typography>
                  <Typography variant="body1">Revenue:</Typography>
                  <Typography variant="body1">Price per Video:</Typography>
                  <Typography variant="body1">Video Limit:</Typography>
                </div>
                <div style={{ textAlign: "right" }}>
                  <Typography variant="body1">{plan.count}</Typography>
                  <Typography variant="body1">
                    ${plan.revenue.toLocaleString()}
                  </Typography>
                  <Typography variant="body1">
                    ${plan.pricePerUGC.toFixed(2)}
                  </Typography>
                  <Typography variant="body1">{plan.videoLimit}</Typography>
                </div>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Tabs for detailed analytics */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          color: THEME_COLORS.yellow,
          "& .MuiTab-root": { color: "black" }, // Inactive tab color
          "& .Mui-selected": { color: THEME_COLORS.yellow }, // Active tab color
          mb: 3,
        }}
      >
        <Tab label="Premium Users" />
        <Tab label="Premium Workspaces" />
        <Tab label="Video Analytics" />
      </Tabs>

      {/* Premium Users Tab */}
      {activeTab === 0 && (
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Organization</TableCell>
                  <TableCell>Plan</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {premiumUsers.map((user: User) => {
                  const premiumWorkspace = user.ownedWorkspaces?.find(
                    (ws) => ws.plan
                  );
                  return (
                    <TableRow key={user._id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.organization?.name || "-"}</TableCell>
                      <TableCell>
                        {premiumWorkspace ? (
                          <Chip
                            label={premiumWorkspace.plan?.name}
                            color="primary"
                            size="small"
                          />
                        ) : (
                          "-"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Premium Workspaces Tab */}
      {activeTab === 1 && (
        <TableContainer component={Paper}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <CircularProgress />
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Workspace</TableCell>
                  <TableCell>Owner</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Videos Generated</TableCell>
                  <TableCell>Members</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {premiumWorkspaces.map((workspace: Workspace) => (
                  <TableRow key={workspace._id}>
                    <TableCell>{workspace.name}</TableCell>
                    <TableCell>{workspace.owner?.name || "-"}</TableCell>
                    <TableCell>
                      <Chip
                        label={workspace.plan?.name}
                        color="primary"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {workspace.generatedVideos || 0} /{" "}
                      {workspace.plan?.videoLimit || "∞"}
                    </TableCell>
                    <TableCell>{workspace.members?.length || 0}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      {/* Video Analytics Tab */}
      {activeTab === 2 && (
        <Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Video Generation Summary
                </Typography>
                <Typography>
                  Total Premium Videos:{" "}
                  {premiumWorkspaces.reduce(
                    (sum, ws) => sum + (ws.generatedVideos || 0),
                    0
                  )}
                </Typography>
                <Typography>
                  Average per Workspace:{" "}
                  {(
                    premiumWorkspaces.reduce(
                      (sum: number, ws) => sum + (ws.generatedVideos || 0),
                      0
                    ) / Math.max(1, premiumWorkspaces.length)
                  ).toFixed(1)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Plan Utilization
                </Typography>
                {planStats.map((plan) => (
                  <Box key={plan.name} sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      {plan.name}:{" "}
                      {(
                        (premiumWorkspaces
                          .filter((ws) => ws.plan?.name === plan.name)
                          .reduce(
                            (sum, ws) => sum + (ws.generatedVideos || 0),
                            0
                          ) /
                          Math.max(1, plan.count * plan.videoLimit)) *
                        100
                      ).toFixed(1)}
                      % of capacity
                    </Typography>
                  </Box>
                ))}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};
